import Vue from 'vue'
import Vuex from 'vuex'

import http from '@/services/http'
import buildProduct from '@/services/buildProduct'

import currentEpisode from '@/assets/currentEpisode'
import currentProducts from '@/assets/currentProducts'

Vue.use(Vuex)

export const state = {
  initialized: false,
  episode: currentEpisode,
  products: currentProducts,
  cart: {
    added: [],
    loaded: false
  },
  eggFound: false
}

export const getters = {
  getProductById: (state) => (id) => state.products.find(it => it.id === id),
  cartItems: (state) => {
    return state.cart.added.map(({ itemId, id, variant, quantity }) => {
      const product = state.products.find(product => product.id === id)
      const productVariant = variant === null ? null : product.variants.find(it => it.id === variant)

      return {
        itemId,
        id: product.id,
        name: product.name,
        price: product.price,
        quantity,
        image: product.primaryPhoto,
        variant: productVariant,
        category: product.category
      }

    })
  },
  shippingTotal: (state, getters) => {
    if (state.eggFound) {
      return 0
    }
    return getters.cartItems.reduce((total, it) => {
      if (it.category.join(', ').toLowerCase().includes('sticker')) {
        return Math.round(total + (it.quantity / 2))
      } else {
        return total + (it.quantity * 4)
      }
    }, 0)
  },
  cartTotal: (state, getters) => {
    return getters.cartItems.reduce((total, item) => {
      return total + item.price * item.quantity
    }, 0)
  },
  stripeTotal: (state, getters) => getters.cartTotal + getters.shippingTotal,
  cartQuantity: (state) => state.cart.added.reduce((total, item) => total + item.quantity, 0)
}

export const mutations = {
  SET_INITIALIZED(state, val) {
    state.initialized = val
  },
  
  SET_EPISODE(state, episode) {
    state.episode = episode
  },

  SET_PRODUCTS(state, products) {
    state.products = [...products]
  },

  ADD_ITEM(state, { id, variant, itemId }) {
    state.cart.added = [...state.cart.added, { itemId, id, variant, quantity: 1 }]

    window.localStorage.setItem('catholicaf.cart', JSON.stringify(state.cart.added))
  },

  INCREMENT_ITEM_QUANTITY(state, { itemId }) {
    // increment the quantity of the item in the cart by 1
    const cartItem = state.cart.added.find(it => it.itemId === itemId)
    cartItem.quantity++

    window.localStorage.setItem('catholicaf.cart', JSON.stringify(state.cart.added))
  },

  DECREMENT_ITEM_QUANTITY(state, { itemId }) {
    // decrement the quantity of the item in the cart by 1
    const cartItem = state.cart.added.find(it => it.itemId === itemId)

    if (cartItem.quantity === 1) {
      // we are going to make quantity zero. remove this item from the cart.
      state.cart.added = state.cart.added.filter(it => it.itemId !== itemId)
    } else {
      // decrement quantity by 1
      cartItem.quantity--
    }

    window.localStorage.setItem('catholicaf.cart', JSON.stringify(state.cart.added))
  },

  SET_CART_LOADED(state, val) {
    state.cart.loaded = val
  },

  SET_CART_ITEMS(state, items) {
    state.cart.added = [...items]
  },

  SET_EGG_FOUND(state, val) {
    state.eggFound = val
  }
}

export const actions = {
  async fetchProducts({ commit, state }) {
    // set products
    let rawProducts = await Promise.all(state.episode.products.map(id => http.get(`/api/products/${id}`)))
    let products = rawProducts.map(it => buildProduct(it.data))

    // populate variants (sizes, etc)
    for (let product of products) {
      if (product.variants) {
        let rawVariants = await Promise.all(product.variants.map(variantId => http.get(`/api/variants/${variantId}`)))
        product.variants = rawVariants.map(v => v.data)
      }
    }

    commit('SET_PRODUCTS', products)
    commit('SET_INITIALIZED', true)
  },

  loadCart({ commit, state }) {
    if (state.cart.loaded === false) {
      let cartItems = window.localStorage.getItem('catholicaf.cart')

      if (cartItems !== null) {
        // if it's not null it will be an array, so we can comfortably assume it's okay to chain .filter()
        cartItems = JSON.parse(cartItems).filter(it => {
          const foundProduct = state.products.find(product => product.id === it.id)
          return foundProduct !== undefined
        })
        commit('SET_CART_ITEMS', cartItems)
      }

      commit('SET_CART_LOADED', true)
    }
  },

  addToCart({ commit, state }, product) {
    const key = product.variant === null ? 'id' : 'variant'
    const cartItem = state.cart.added.find(item => item[key] === product[key])

    if (!cartItem) {
      commit('ADD_ITEM', {
        itemId: Date.now(),
        id: product.id,
        variant: product.variant
      })
    } else {
      commit('INCREMENT_ITEM_QUANTITY', cartItem)
    }
  },

  incrementItemQuantity({ commit }, item) {
    commit('INCREMENT_ITEM_QUANTITY', item)
  },

  decrementItemQuantity({ commit }, item) {
    commit('DECREMENT_ITEM_QUANTITY', item)
  },
  clearCart({ commit }) {
    // remove cart items from localStorage
    window.localStorage.removeItem('catholicaf.cart')
    // clear items in cart
    commit('SET_CART_ITEMS', [])
  }
}

export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions
})
